// KYC image pre-check. Runs in the browser before a document photo is sent
// to the backend, so bad photos (blur, glare, cut off, too dark, too small,
// re-sent) do not cost a paid verification attempt.
// Record: docs/changes/2026-10-08-migrate-from-onfido-api-to-didit-api-for-kyc.md

import { THRESHOLDS, MAX_REJECTIONS_BEFORE_OVERRIDE } from "./thresholds";
import { loadImage } from "./loadImage";
import {
  toGrey,
  measureBrightness,
  measureBlur,
  measureGlare,
  fileHash,
} from "./pixelChecks";
import { loadOpenCv, detectCard } from "./cardDetect";

export { loadOpenCv as preloadImageQuality };

// i18n key -> English fallback shown until the key is translated.
export const ISSUE_MESSAGES = {
  imageQualityTooSmall:
    "The photo is too small. Move closer so the document fills the frame.",
  imageQualityTooDark: "The photo is too dark. Move to a brighter place.",
  imageQualityTooBright:
    "The photo is too bright. Avoid direct light on the document.",
  imageQualityBlurry:
    "The photo is blurry. Hold steady and focus on the document.",
  imageQualityGlare:
    "There is glare on the document. Tilt it away from the light.",
  imageQualityCutOff:
    "Part of the document is cut off. Make sure all four corners are visible.",
  imageQualityNoDocument:
    "We could not find the document. Place it on a plain, dark surface inside the frame.",
  imageQualitySamePhoto:
    "This is the same photo as before. Please take a new one.",
  imageQualityChecking: "Checking the photo...",
  imageQualityUseAnyway: "Use anyway",
};

// Translated text, or the English fallback while the key is untranslated.
export const qualityText = (_t, key) => {
  const text = _t(key);
  return text && text !== key ? text : ISSUE_MESSAGES[key];
};

const resultCache = new WeakMap();
const rejections = { front: 0, back: 0 };
const rejectedHashes = [];

// mode: "camera" | "upload". side: "front" | "back".
// Resolves to { passed, issues, canOverride, metrics, durationMs }.
export const checkImageQuality = (
  blob,
  { mode = "upload", side = "front" } = {}
) => {
  if (resultCache.has(blob)) return resultCache.get(blob);
  const promise = runChecks(blob, mode, side);
  resultCache.set(blob, promise);
  return promise;
};

const runChecks = async (blob, mode, side) => {
  const started = performance.now();
  const t = { ...THRESHOLDS, ...THRESHOLDS[mode] };
  const issues = [];
  const metrics = { mode, side };

  let loaded;
  try {
    loaded = await loadImage(blob);
  } catch {
    // Cannot be decoded here; let the existing upload flow handle it.
    return { passed: true, issues, canOverride: false, metrics, durationMs: 0 };
  }
  const { canvas, originalWidth, originalHeight } = loaded;
  metrics.originalSize = `${originalWidth}x${originalHeight}`;

  // 1. Size
  if (Math.min(originalWidth, originalHeight) < t.minShortSide) {
    issues.push("imageQualityTooSmall");
  }

  // 2. Find the document (OpenCV). Skipped if OpenCV did not load.
  const cv = await loadOpenCv();
  let card = null;
  if (cv) {
    try {
      card = detectCard(cv, canvas, t);
    } catch (err) {
      console.error("Card detection failed:", err);
    }
  }
  metrics.opencv = Boolean(cv);
  metrics.card = card && {
    found: card.found,
    cutOff: Boolean(card.cutOff),
    anyShape: card.anyShape,
    areaRatio: card.areaRatio && round(card.areaRatio),
    tightCrop: card.tightCrop,
  };

  if (card) {
    if (card.cutOff) {
      issues.push("imageQualityCutOff");
    } else if (!card.found && !card.anyShape && mode === "camera") {
      // Nothing document-sized at all. Not used for uploads: they are often
      // already cropped to the card, with no visible edge.
      issues.push("imageQualityNoDocument");
    }
  }

  // Measure light, blur and glare on the document only, when we found it.
  const box = card && card.found && !card.tightCrop ? card.box : null;
  const greyImage = toGrey(
    canvas
      .getContext("2d", { willReadFrequently: true })
      .getImageData(0, 0, canvas.width, canvas.height)
  );

  // 3. Brightness
  const brightness = measureBrightness(greyImage, box);
  metrics.brightness = round(brightness);
  if (brightness < t.minBrightness) issues.push("imageQualityTooDark");
  else if (brightness > t.maxBrightness) issues.push("imageQualityTooBright");

  // 4. Blur
  const blur = measureBlur(greyImage, box);
  metrics.blurVariance = round(blur);
  if (blur < t.minBlurVariance) issues.push("imageQualityBlurry");

  // 5. Glare, only on a document we found: on the whole photo, windows and
  // lamps in the background would count as glare.
  if (card && card.found) {
    const glare = measureGlare(greyImage, box, t.glarePixelValue);
    metrics.glareRatio = round(glare.ratio);
    metrics.glareWorstCell = round(glare.worstCell);
    if (
      glare.ratio > t.maxGlareRatio ||
      glare.worstCell > t.maxGlareCellRatio
    ) {
      issues.push("imageQualityGlare");
    }
  }

  // 6. Exactly the same file as one already rejected
  const hash = await fileHash(blob);
  if (hash && rejectedHashes.includes(hash)) {
    issues.push("imageQualitySamePhoto");
  }

  const passed = issues.length === 0;
  if (passed) {
    rejections[side] = 0;
  } else {
    rejections[side] += 1;
    if (hash) rejectedHashes.push(hash);
  }

  const durationMs = Math.round(performance.now() - started);
  metrics.durationMs = durationMs;
  metrics.issues = issues;

  return {
    passed,
    issues,
    canOverride: !passed && rejections[side] >= MAX_REJECTIONS_BEFORE_OVERRIDE,
    metrics,
    durationMs,
  };
};

const round = (n) => Math.round(n * 1000) / 1000;
